import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Send } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DemoNotice from "@/components/demo/DemoNotice";
import { Button } from "@/components/ui/button";
import AthleteHubHome, { type Hub } from "@/components/athlete/AthleteHubHome";
import { DEMO_PROFILE_PATH, demoAthlete as a } from "@/data/demoAthlete";

/**
 * Sales demo of the athlete Business Hub. Renders the real dashboard Home in
 * its in-memory fixture mode (reads nothing, writes nothing). Not in site nav.
 */
const athlete = {
  id: a.id, slug: a.slug, name: a.name, sport: a.sport, tagline: a.tagline,
  bio: a.story.map((p) => `<p>${p}</p>`).join(""), image_path: "demo.jpg",
  instagram_url: "https://instagram.com/example", partnership_interests: a.partnershipInterests,
  published: true, support_enabled: true, payment_provider: "ko-fi", payment_url: "https://ko-fi.com/example",
} as any;

const hub: Hub = {
  stage: "published", quick_start_submitted_at: "2026-09-20T12:00:00Z",
  module_choices: { highlights: false, causes: true, work_with: true, her_collection: "interested" } as any,
  approved_current_version: true, approved_at: "2026-09-28T12:00:00Z",
  consents: { profile_publication: true, photo_likeness: true }, follower_count: 128, changes_requested: null,
  inquiries: [
    { id: "d1", company: "Sample Hydration Co.", opportunity_type: "Brand ambassador", estimated_budget: "$2,500–$10,000", timing: "Spring season", description: "Sample inquiry: season-long ambassador content.", status: "new", created_at: "2026-10-09T12:00:00Z" },
    { id: "d2", company: "Sample Youth League", opportunity_type: "Appearance", estimated_budget: "$500–$2,500", timing: "November", description: "Sample inquiry: clinic appearance.", status: "reviewed", created_at: "2026-10-01T12:00:00Z" },
  ],
};

const card = "bg-card rounded-2xl p-5 shadow-card";
const pill = "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

const AthleteBusinessHubDemo = () => {
  const fixture = useMemo(() => ({ hub, shopStatus: "live" }), []);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sent, setSent] = useState(false);

  const submitUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Add a title for the update.");
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Navbar />
      <DemoNotice hub />

      <main className="container py-8 md:py-12 space-y-6">
        <div>
          <h1 className="sr-only">Athlete Business Hub demo</h1>
          <p className=" max-w-2xl text-sm text-muted-foreground">
            Athletes complete Quick Start, SIIS structures the profile, the athlete approves the exact version, and SIIS publishes it.
          </p>
        </div>

        <AthleteHubHome athlete={athlete} causeCount={1} onGo={() => toast("Demo: this would open that dashboard tab.")} fixture={fixture} />

        <div className="grid gap-4 md:grid-cols-2">
          <form onSubmit={submitUpdate} className={`${card} space-y-3`}>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Draft a My Corner update</h2>
            {sent ? (
              <div role="status" className="rounded-xl bg-muted p-4 text-sm">
                <p className="font-semibold">Sample draft previewed. Nothing was saved, sent to SIIS or shown in My Corner.</p>
                <button type="button" className="mt-2 font-semibold text-primary-strong underline min-h-[44px]" onClick={() => { setSent(false); setTitle(""); setBody(""); }}>
                  Draft another
                </button>
              </div>
            ) : (
              <>
                <label className="block text-sm font-semibold" htmlFor="demo-update-title">Title</label>
                <input id="demo-update-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm min-h-[44px]" placeholder="Back in the starting XI" />
                <label className="block text-sm font-semibold" htmlFor="demo-update-body">Update</label>
                <textarea id="demo-update-body" value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={1000}
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm" placeholder="Tell your corner what's new…" />
                <Button type="submit" className={pill}>
                  <Send className="h-4 w-4" aria-hidden /> Preview sample draft
                </Button>
              </>
            )}
          </form>

          <div className={`${card} space-y-3`}>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Share &amp; review profile</h2>
            <p className="text-sm text-muted-foreground">See exactly what fans, teams and brands see, then share it anywhere.</p>
            <Link to={DEMO_PROFILE_PATH} className={`${pill} bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground`}>
              <Eye className="h-4 w-4" aria-hidden /> View demo profile
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AthleteBusinessHubDemo;
